import { useState } from 'react';
import { supabase } from '../lib/supabase.ts';
import { friendlyError } from '../lib/friendlyError.ts';
import { btnSolid, input as inputCls } from '../lib/ui.ts';
import { FlapText } from '../components/FlapText.tsx';

type Mode = 'signin' | 'forgot';

export function Login() {
  const [mode, setMode] = useState<Mode>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  async function signIn(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setNotice(null);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) setError(friendlyError(error, error.message));
    setBusy(false);
  }

  async function sendReset(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setNotice(null);
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      // Supabase appends the recovery token; landing back here triggers the
      // PASSWORD_RECOVERY event, which App handles by showing the reset screen.
      redirectTo: window.location.origin,
    });
    // Always show the same neutral confirmation — don't leak which emails exist.
    if (error) setError(friendlyError(error, error.message));
    else
      setNotice(
        'If that email has an account, a password-reset link is on its way. ' +
          'Open it on this device to set a new password.',
      );
    setBusy(false);
  }

  return (
    <div className="flex min-h-screen items-center justify-center px-4 text-ink">
      <form
        onSubmit={mode === 'signin' ? signIn : sendReset}
        className="card w-full max-w-sm space-y-8 p-8"
      >
        {/* Brand lockup — the Sorts Mill Goudy wordmark (via font-display), uppercase (brand §1). */}
        <div className="space-y-3 text-center">
          <div className="flex items-center justify-center gap-3">
            <h1 className="whitespace-nowrap font-display text-wordmark uppercase tracking-[-0.02em] leading-none text-ink">Alchmy Build</h1>
          </div>
          {mode === 'forgot' && <p className="font-serif text-lg text-ink-2">Reset your password</p>}
        </div>

        <div className="space-y-3">
          <input
            type="email"
            required
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={`w-full ${inputCls}`}
          />

          {mode === 'signin' && (
            <input
              type="password"
              required
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={`w-full ${inputCls}`}
            />
          )}

          <button type="submit" disabled={busy} className={`w-full ${btnSolid}`}>
            <FlapText>{busy ? 'Working…' : mode === 'signin' ? 'Sign in' : 'Send reset link'}</FlapText>
          </button>
        </div>

        {error && <p className="text-sm text-danger">{error}</p>}
        {notice && <p className="text-sm text-success">{notice}</p>}

        <div className="text-center">
          <button
            type="button"
            onClick={() => {
              setMode(mode === 'signin' ? 'forgot' : 'signin');
              setError(null);
              setNotice(null);
            }}
            className="text-xs text-ink-2 underline underline-offset-2 hover:text-ink"
          >
            {mode === 'signin' ? 'Forgot password?' : 'Back to sign in'}
          </button>
        </div>
      </form>
    </div>
  );
}
